import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Check,
  CreditCard,
  Calendar,
  AlertTriangle,
  Crown,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import UsagePanel from '../../components/subscription/UsagePanel';
import { aiCreditsService, reais } from '../../services/aiCreditsService';
import { Plan, Subscription, Invoice } from '../../types';

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [currency, setCurrency] = useState<'brl' | 'usd'>('brl');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'plans' | 'usage' | 'invoices'>('plans');
  const [aiAllowances, setAiAllowances] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    aiCreditsService.planAllowances().then(setAiAllowances).catch(() => setAiAllowances(null));
  }, []);

  useEffect(() => {
    loadData();

    // Handle Stripe checkout redirect
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId) {
      toast.success(t('subscription.paymentSuccess') || 'Payment successful! Your subscription is now active.');
    } else if (canceled === 'true') {
      toast.error(t('subscription.paymentCanceled') || 'Payment was canceled.');
    }
  }, [searchParams]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [plansRes, subRes, invoicesRes] = await Promise.all([
        subscriptionService.getPlans(),
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getInvoices()
      ]);

      if (plansRes.success && plansRes.data) {
        // Filter out custom plan and inactive plans
        const filteredPlans = plansRes.data.filter((p: Plan) => p.slug !== 'custom' && p.is_active !== false);
        setPlans(filteredPlans);
      }
      if (subRes.success && subRes.data) {
        setSubscription(subRes.data);
        // Set currency and billing cycle from existing subscription
        if (subRes.data.currency) {
          setCurrency(subRes.data.currency);
        }
        if (subRes.data.billing_cycle) {
          setBillingCycle(subRes.data.billing_cycle);
        }
      }
      if (invoicesRes.success && invoicesRes.data) {
        setInvoices(invoicesRes.data);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = (plan: Plan) => {
    if (subscription?.plan_id === plan.id && subscription.billing_cycle === billingCycle) return;
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    setIsProcessing(true);
    try {
      // Check if it's the free plan
      const isFreePlan = selectedPlan.price_monthly === 0 && selectedPlan.price_yearly === 0;

      // If user already has an active Stripe subscription, change plan instead
      // Users with admin-assigned plans (no stripe_subscription_id) need to go through checkout
      if (subscription && subscription.status === 'active' && subscription.stripe_subscription_id) {
        const response = await subscriptionService.changePlan(selectedPlan.id, billingCycle, currency);
        if (response.success) {
          toast.success(t('subscription.planChanged') || 'Plan changed successfully!');
          setIsModalOpen(false);
          loadData();
        }
      } else if (isFreePlan) {
        // Free plan - create subscription directly without checkout
        const response = await subscriptionService.createSubscription({
          plan_id: selectedPlan.id,
          billing_cycle: 'monthly',
          currency
        });
        if (response.success) {
          toast.success(t('subscription.freePlanActivated') || 'Free plan activated!');
          setIsModalOpen(false);
          loadData();
        }
      } else {
        // Paid plan - go to checkout
        const response = await subscriptionService.createSubscription({
          plan_id: selectedPlan.id,
          billing_cycle: billingCycle,
          currency
        });

        if (response.success && response.data?.url) {
          window.location.href = response.data.url;
        } else {
          toast.success(t('messages.updatedSuccessfully'));
          setIsModalOpen(false);
          loadData();
        }
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    try {
      await subscriptionService.cancelSubscription();
      toast.success(t('subscription.cancelledSuccessfully'));
      setIsCancelModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsProcessing(true);
    try {
      await subscriptionService.reactivateSubscription();
      toast.success(t('subscription.reactivatedSuccessfully') || 'Subscription reactivated successfully!');
      loadData();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await subscriptionService.getPortalUrl();
      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const getPrice = (plan: Plan) => {
    let price: number;
    if (currency === 'usd') {
      price = billingCycle === 'monthly'
        ? ((plan as any).price_usd_monthly || 0)
        : ((plan as any).price_usd_yearly || 0);
      return Number(price || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    } else {
      price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
      // a API pode mandar o preço como texto ("0.00"); sem Number() sai sem R$ e com ponto
      return Number(price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray'> = {
      active: 'success',
      trialing: 'warning',
      past_due: 'danger',
      canceled: 'gray',
      inactive: 'gray'
    };
    return variants[status] || 'gray';
  };

  const getInvoiceStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray'> = {
      paid: 'success',
      open: 'warning',
      void: 'gray',
      uncollectible: 'danger'
    };
    return variants[status] || 'gray';
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="space-y-3 sm:space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-96 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <PageHeader
        icon={Crown}
        title={t('subscription.pageTitle', 'Plano')}
        description={t('subscription.pageSubtitle', 'Veja seu plano, quanto já usou e troque de plano quando quiser.')}
        actions={
          <Button variant="secondary" size="sm" onClick={loadData} aria-label={t('common.refresh')}>
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.refresh')}</span>
          </Button>
        }
      />

      {/* Current Subscription */}
      {subscription && (
        <Card padding={false}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-6">
              {/* Plan Info */}
              <div className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{subscription.plan?.name}</h3>
                    <Badge variant={getStatusBadge(subscription.status)}>
                      {t(`subscription.status.${subscription.status}`)}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t(`subscription.billingCycle.${subscription.billing_cycle}`)}
                    {subscription.current_period_end && (
                      <>
                        {' • '}
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {subscription.cancel_at_period_end
                            ? `${t('subscription.endsOn', 'Ends on')} ${new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}`
                            : `${t('subscription.renewsOn')} ${new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}`
                          }
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Only show Manage Billing if user has Stripe subscription */}
                {subscription.stripe_subscription_id && (
                  <Button variant="secondary" size="sm" onClick={handleManageBilling}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t('subscription.manageBilling')}
                  </Button>
                )}
                {subscription.status === 'active' && !subscription.cancel_at_period_end && subscription.stripe_subscription_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => setIsCancelModalOpen(true)}
                  >
                    {t('subscription.cancel')}
                  </Button>
                )}
                {subscription.cancel_at_period_end && subscription.stripe_subscription_id && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleReactivateSubscription}
                    isLoading={isProcessing}
                  >
                    {t('subscription.reactivate')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('plans')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'plans'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('admin.plans')}
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'usage'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('subscription.usage.title', { defaultValue: 'Uso do Plano' })}
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invoices'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('subscription.invoices')}
          </button>
        </nav>
      </div>

      {activeTab === 'usage' && (
        <UsagePanel onUpgradeClick={() => setActiveTab('plans')} />
      )}

      {activeTab === 'plans' && (
        <>
          {/* Currency & Billing Cycle Toggle */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            {/* Currency Toggle - Only show if no active subscription */}
            {!subscription || !subscription.status || subscription.status === 'canceled' ? (
              <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    currency === 'brl'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  onClick={() => setCurrency('brl')}
                >
                  <span>🇧🇷</span> BRL
                </button>
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    currency === 'usd'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  onClick={() => setCurrency('usd')}
                >
                  <span>🇺🇸</span> USD
                </button>
              </div>
            ) : (
              /* Show current subscription currency as badge */
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300">
                <span>{currency === 'usd' ? '🇺🇸' : '🇧🇷'}</span>
                <span>{currency.toUpperCase()}</span>
              </div>
            )}

            {/* Billing Cycle Toggle */}
            <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                onClick={() => setBillingCycle('monthly')}
              >
                {t('subscription.monthly')}
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                onClick={() => setBillingCycle('yearly')}
              >
                {t('subscription.yearly')}
                <span className="ml-2 text-xs text-green-600 font-semibold">-17%</span>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 pt-4">
            {plans.map((plan) => {
              const isCurrentPlan = subscription?.plan_id === plan.id && subscription?.billing_cycle === billingCycle;
              const isPopular = plan.slug === 'professional';
              const isFreePlan = plan.price_monthly === 0 && plan.price_yearly === 0;
              const displayLimit = (limit: number | undefined) => limit === -1 ? '∞' : (limit || 0);

              return (
                <Card
                  key={plan.id}
                  className={`relative flex flex-col !overflow-visible ${isPopular ? 'ring-2 ring-primary-600' : ''} ${
                    isCurrentPlan ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                      <Badge variant="primary">{t('subscription.mostPopular')}</Badge>
                    </div>
                  )}
                  <CardContent className="pt-8 flex flex-col flex-1">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 min-h-[40px]">{plan.description}</p>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">{getPrice(plan)}</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          /{billingCycle === 'monthly' ? t('subscription.month') : t('subscription.year')}
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-2.5 flex-1">
                      {plan.limits && (
                        <>
                          <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span><strong>{displayLimit(plan.limits.projects_limit || plan.limits.projects)}</strong> {t('subscription.features.projects', 'projects')}</span>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span><strong>{displayLimit(plan.limits.campaigns_limit || plan.limits.campaigns)}</strong> {t('subscription.features.campaigns')}</span>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span><strong>{displayLimit(plan.limits.keywords_limit || plan.limits.keywords)}</strong> {t('subscription.features.keywords')}</span>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span><strong>{displayLimit(plan.limits.content_generations_limit || plan.limits.content_generations)}</strong> {t('subscription.features.contentGenerations')}</span>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span><strong>{displayLimit(plan.limits.team_members_limit || plan.limits.team_members)}</strong> {t('subscription.features.teamMembers', 'team members')}</span>
                          </li>
                          {plan.limits.ai_enabled && (
                            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span>{t('subscription.features.aiContent')}</span>
                            </li>
                          )}
                          {aiAllowances && aiAllowances[plan.slug] != null && (
                            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span>{t('subscription.features.aiCredits', '{{valor}} em créditos de IA por mês', { valor: reais(aiAllowances[plan.slug]) })}</span>
                            </li>
                          )}
                          {plan.limits.api_access && (
                            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span>{t('subscription.features.apiAccess')}</span>
                            </li>
                          )}
                          {(plan.limits as any)?.image_studio_enabled && (
                            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span>{t('subscription.features.imageStudioCredits', 'Imagens com IA (pelos créditos de IA)')}</span>
                            </li>
                          )}
                        </>
                      )}
                    </ul>

                    <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <Button
                        className="w-full"
                        variant={isCurrentPlan ? 'secondary' : isPopular ? 'primary' : 'secondary'}
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isCurrentPlan}
                      >
                        {isCurrentPlan ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('subscription.currentPlan')}
                          </>
                        ) : isFreePlan ? t('subscription.startFree', 'Start Free') : subscription?.stripe_subscription_id ? t('subscription.changePlan') : t('subscription.selectPlan')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {activeTab === 'invoices' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t('subscription.invoices')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left px-2 sm:px-4 py-2 sm:py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('common.date')}
                      </th>
                      <th className="text-left px-2 sm:px-4 py-2 sm:py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('admin.amount')}
                      </th>
                      <th className="text-left px-2 sm:px-4 py-2 sm:py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('common.status')}
                      </th>
                      <th className="text-right px-2 sm:px-4 py-2 sm:py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {new Date(invoice.created).toLocaleDateString('pt-BR')}
                          </div>
                          {invoice.number && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              #{invoice.number}
                            </div>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(invoice.amount_paid, invoice.currency)}
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <Badge variant={getInvoiceStatusBadge(invoice.status)}>
                            {t(`subscription.invoiceStatus.${invoice.status}`, invoice.status)}
                          </Badge>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {invoice.hosted_invoice_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.invoice_pdf && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscribe/Change Plan Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={subscription?.stripe_subscription_id ? t('subscription.changePlan') : t('subscription.confirmSubscription')}
      >
        {selectedPlan && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white">{selectedPlan.name}</h4>
              <p className="text-2xl font-bold text-primary-600 mt-2">
                {getPrice(selectedPlan)}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  /{billingCycle === 'monthly' ? t('subscription.month') : t('subscription.year')}
                </span>
              </p>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {subscription?.stripe_subscription_id
                ? (t('subscription.changePlanMessage') || 'Your plan will be updated immediately. Pricing will be prorated.')
                : t('subscription.confirmMessage')
              }
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubscribe} isLoading={isProcessing}>
                {subscription?.stripe_subscription_id ? t('subscription.changePlan') : t('subscription.subscribe')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title={t('subscription.cancelSubscription')}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              {t('subscription.cancelWarning')}
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsCancelModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancelSubscription}
              isLoading={isProcessing}
            >
              {t('subscription.confirmCancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPage;
